import { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";

import type RFB from "@novnc/novnc";

type RfbEvent = Event & {
  readonly detail?: {
    readonly clean?: boolean;
    readonly reason?: string;
  };
};

export interface VmDesktopRfbProps {
  readonly url: string;
  readonly protocols: readonly [string, string];
  readonly password: string;
  readonly viewOnly: boolean;
  readonly onConnect: () => void;
  readonly onDisconnect: (error?: Error) => void;
}

type RfbConstructor = typeof RFB;

let noVncModulePromise: Promise<RfbConstructor> | null = null;

/** A broken/unsupported VM should return to the screenshot fallback promptly. */
export const RFB_HANDSHAKE_TIMEOUT_MS = 15_000;

/**
 * Keep noVNC out of the default chat bundle. The module is loaded only after
 * the VM surface has received an admitted stream URL.
 */
export function loadNoVnc(): Promise<RfbConstructor> {
  if (noVncModulePromise === null) {
    let cachedPromise: Promise<RfbConstructor>;
    cachedPromise = import("@novnc/novnc")
      .then((module) => module.default)
      .catch((error: unknown) => {
        if (noVncModulePromise === cachedPromise) noVncModulePromise = null;
        throw error;
      });
    noVncModulePromise = cachedPromise;
  }
  return noVncModulePromise;
}

function errorFromDisconnect(event: Event): Error | undefined {
  const detail = (event as RfbEvent).detail;
  if (detail?.clean !== false && !detail?.reason) return undefined;
  return new Error(detail?.reason ?? "The VM desktop stream disconnected.");
}

function errorFromUnknown(error: unknown): Error {
  return error instanceof Error ? error : new Error("Could not start the VM desktop stream.");
}

export interface VmDesktopRfbConnection {
  readonly rfb: RFB;
  readonly cleanup: () => void;
}

export function connectVmDesktopRfb(input: {
  readonly target: HTMLElement;
  readonly Rfb: RfbConstructor;
  readonly url: string;
  readonly protocols: readonly [string, string];
  readonly password: string;
  readonly viewOnly: boolean;
  readonly onConnect: () => void;
  readonly onDisconnect: (error?: Error) => void;
  readonly handshakeTimeoutMs?: number;
}): VmDesktopRfbConnection {
  const socket = new WebSocket(input.url, [...input.protocols]);
  let rfb: RFB;
  try {
    rfb = new input.Rfb(input.target, socket, { shared: true });
  } catch (error) {
    // noVNC can throw after it has been handed the socket. Close that socket
    // here so a failed constructor cannot leave an unauthenticated connection
    // running behind the screenshot fallback.
    try {
      socket.close();
    } catch {
      // The browser may already have transitioned the socket to CLOSED.
    }
    throw error;
  }
  rfb.scaleViewport = true;
  rfb.resizeSession = false;
  rfb.viewOnly = input.viewOnly;
  rfb.background = "rgb(0, 0, 0)";
  let handshakeTimer: ReturnType<typeof setTimeout> | null = null;
  let connected = false;
  let notifiedDisconnect = false;

  const clearHandshakeTimer = () => {
    if (handshakeTimer !== null) {
      globalThis.clearTimeout(handshakeTimer);
      handshakeTimer = null;
    }
  };
  const notifyDisconnect = (error?: Error) => {
    if (notifiedDisconnect) return;
    notifiedDisconnect = true;
    clearHandshakeTimer();
    input.onDisconnect(error);
  };
  const handleCredentialsRequired = () => {
    rfb.sendCredentials({ password: input.password });
  };
  const handleDisconnect = (event: Event) => {
    notifyDisconnect(errorFromDisconnect(event));
  };
  const handleConnect = () => {
    if (notifiedDisconnect) return;
    connected = true;
    clearHandshakeTimer();
    input.onConnect();
  };
  rfb.addEventListener("connect", handleConnect);
  rfb.addEventListener("credentialsrequired", handleCredentialsRequired);
  rfb.addEventListener("disconnect", handleDisconnect);
  handshakeTimer = globalThis.setTimeout(() => {
    if (connected || notifiedDisconnect) return;
    const error = new Error("The VM desktop stream handshake timed out.");
    notifyDisconnect(error);
    try {
      rfb.disconnect();
    } catch {
      // noVNC may already have transitioned to its terminal state.
    }
  }, input.handshakeTimeoutMs ?? RFB_HANDSHAKE_TIMEOUT_MS);

  return {
    rfb,
    cleanup: () => {
      notifiedDisconnect = true;
      clearHandshakeTimer();
      rfb.removeEventListener("connect", handleConnect);
      rfb.removeEventListener("credentialsrequired", handleCredentialsRequired);
      rfb.removeEventListener("disconnect", handleDisconnect);
      try {
        rfb.disconnect();
      } catch {
        // noVNC may already have transitioned to its terminal state.
      }
    },
  };
}

/**
 * The noVNC canvas owns all RFB pointer and keyboard listeners. The parent
 * surface deliberately renders no legacy input handlers while this child is
 * mounted, avoiding duplicate input over the screenshot fallback.
 */
export function VmDesktopRfb({
  url,
  protocols,
  password,
  viewOnly,
  onConnect,
  onDisconnect,
}: VmDesktopRfbProps) {
  const targetRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    let rfb: RFB | null = null;
    let cleanupConnection: (() => void) | null = null;

    const connect = async () => {
      const target = targetRef.current;
      if (!target) return;

      try {
        const Rfb = await loadNoVnc();
        if (disposed || targetRef.current !== target) return;

        const connection = connectVmDesktopRfb({
          target,
          Rfb,
          url,
          protocols,
          password,
          viewOnly,
          onConnect,
          onDisconnect: (error) => {
            if (!disposed) onDisconnect(error);
          },
        });
        rfb = connection.rfb;
        cleanupConnection = connection.cleanup;
        rfbRef.current = rfb;
        setLoading(false);
      } catch (error) {
        if (!disposed) onDisconnect(errorFromUnknown(error));
      }
    };

    void connect();
    return () => {
      disposed = true;
      cleanupConnection?.();
      rfbRef.current = null;
    };
  }, [onConnect, onDisconnect, password, protocols, url]);

  useEffect(() => {
    if (rfbRef.current !== null) rfbRef.current.viewOnly = viewOnly;
  }, [viewOnly]);

  return (
    <div
      ref={targetRef}
      className={cn(
        "flex size-full min-h-0 min-w-0 items-center justify-center",
        loading && "opacity-0",
      )}
      data-remote-desktop-rfb
      aria-label="Live VM desktop"
    />
  );
}
